import { ThemeService } from '@core/services/theme.service';
import { ScrollLockService } from '@core/services/scroll-lock.service';
import {
  Component,
  OnInit,
  HostListener,
  OnDestroy,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { User } from 'src/app//core/models/user';
import { UserService } from '@core/services/user.service';
import { Company } from '@core/models/company';
import { PARTNER_PLAN_MESSAGE, isChannelPartnerCompany } from '@core/utils/channel-partner';
import { CompanyService } from '@core/services/company.service';
import { TenableApiService } from '@core/tenable/service/tenable-api.service';
import { Observable, map, isObservable, Subscription, catchError, of } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { TierAccessService, Feature } from '@core/services/tier-access.service';
import { SafeStorageService } from '@core/services/safe-storage.service';
import { NinjaCreditsService, CreditSummary } from '@core/services/ninja-credits.service';
import { NinjaAgentApiService } from '@core/services/ninja-agent-api.service';
import { environment } from 'src/environments/environment';
import sidebarLinksData from '@data/sidebar-links.json';

import { Output, EventEmitter } from '@angular/core';
import { planDisplayName } from '@core/utils/plan-display-name';

/**
 * Raw shape of sidebar link entries from the canonical JSON file.
 * Feature/addon fields are strings that get mapped to enum values at runtime.
 */
interface RawSidebarLink {
  title: string;
  route: string | string[];
  iconImg: string;
  count?: string | null;
  countType?: string;
  future?: boolean;
  isVisable?: boolean;
  requiredFeature?: string;
  requiredAddon?: string;
  sub_links?: RawSidebarLink[];
}

export interface SidebarLink {
  title: string;
  route: string | string[];
  iconImg: string;
  count: Observable<string> | string | null;
  future?: boolean;
  isVisable?: boolean;
  requiredFeature?: Feature;
  requiredAddon?: string;
  sub_links?: SidebarSubLink[];
}

export interface SidebarSubLink extends SidebarLink {
  sub_links?: SidebarSubLink[];
}

@Component({
  selector: 'app-page-sidebar',
  templateUrl: './page-sidebar.component.html',
  styleUrl: './page-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PageSidebarComponent implements OnInit, OnDestroy {
  private themeService = inject(ThemeService);
  private scrollLock = inject(ScrollLockService);
  private companyService = inject(CompanyService);
  private userService = inject(UserService);
  private tenableApiService = inject(TenableApiService);
  private router = inject(Router);
  private tierAccessService = inject(TierAccessService);
  private safeStorage = inject(SafeStorageService);
  private ninjaAgentApiService = inject(NinjaAgentApiService);
  public ninjaCreditsService = inject(NinjaCreditsService);

  sideBarvisible = false;
  isMobile = false;
  // Desktop collapsed (icons-only) mode
  collapsed = false;
  @Output() collapsedChange = new EventEmitter<boolean>();
  private subscriptions: Subscription[] = [];
  private readonly MOBILE_BREAKPOINT = 1024; // Match your existing breakpoint
  public sidebarLinks: SidebarLink[] = [];
  userModel: User | null = null;
  companyModel!: Company;

  activeMenu: string | null = null;
  activeSubMenu: string | null = null;

  // Tier access properties
  currentTier: 'free' | 'trial' | 'premium' | 'expired' = 'free';

  /** Channel-partner company (#943): no upgrade prompts, only the partner note. */
  isPartnerCompany = false;
  readonly partnerPlanMessage = PARTNER_PLAN_MESSAGE;
  tierDisplayName: string = 'No Plan';
  tierBadgeClass: string = 'tier-free';
  dailyAiQueriesRemaining: number = 0;
  dailyAiQueriesTotal: number = 0;
  trialDaysRemaining: number | null = null;

  /** Cached credit summary for the sidebar credit widget */
  creditSummary: CreditSummary | null = null;

  // Centralize your fallback image here:
  defaultImageUrl = 'assets/images/buildings.jpg';

  private _scannersCount$: Observable<string>; // Private variable for the Observable
  private _scanListCount$: Observable<string>; // Private variable for the Observable
  private _ninjaScanListCount$: Observable<string>; // Private variable for the Observable
  private _assetCount$: Observable<string>; // Private variable for the Observable

  // TODO Add a check to see if keys exist in the KV before making any call
  constructor() {
    // Initialize collapsed state from persisted preference
    const persisted = this.safeStorage.getItem('sidebarCollapsed');
    if (persisted !== null) {
      this.collapsed = persisted === 'true';
    }
    this._scannersCount$ = this.getScannersCount(); // Initialize the Observable
    this._scanListCount$ = this.getScanListCount(); // Initialize the Observable
    this._ninjaScanListCount$ = this.getNinjaScanListCount();
    this._assetCount$ = this.getAssetCount();

    this.themeService.getSidebarIsVisable().subscribe((value: boolean) => {
      this.sideBarvisible = value;
    });

    this.userService.getUserModel().subscribe((user) => {
      if (user) {
        this.userModel = user;
        if (this.userModel.currentCompanyId) {
          this.companyService
            .getDocument(this.userModel.currentCompanyId)
            .then((companyProfile: any) => {
              if (companyProfile) {
                this.companyService.company = new Company(companyProfile);
                this.companyModel = new Company(companyProfile);
              }
            });
        }
      }
    });

    this.themeService.getSidebarCompanyModel().subscribe((company: Company) => {
      if (company) {
        this.companyModel = company;
      }
    });

    // Subscribe to router events to close sidebar on mobile navigation
    const routerSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.handleNavigationEnd();
      }
    });
    this.subscriptions.push(routerSub);

    // Subscribe to sidebar visibility changes
    const sidebarSub = this.themeService
      .getSidebarIsVisable()
      .subscribe((value: boolean) => {
        this.sideBarvisible = value;
        this.handleSidebarVisibilityChange(value);
      });
    this.subscriptions.push(sidebarSub);
  }

  ngOnInit(): void {
    this.checkIfMobile();
    this.sidebarLinks = this.buildSidebarLinks();
    this.setActiveMenuByRouteUrl();
    // Sync initial collapsed state
    setTimeout(() => {
      this.collapsedChange.emit(this.collapsed);
      window.dispatchEvent(
        new CustomEvent('sidebar:collapsed', {
          detail: { collapsed: this.collapsed },
        }),
      );
    });

    // Subscribe to company changes for tier tracking
    const companySub = this.companyService.getCurrentUserCompany().subscribe((company: Company | null) => {
      if (company) {
        this.updateTierInfo(company);
      }
    });
    this.subscriptions.push(companySub);

    // Subscribe to credit balance for the sidebar credit widget
    const creditSub = this.ninjaCreditsService.creditSummary$.subscribe((summary) => {
      this.creditSummary = summary;
    });
    this.subscriptions.push(creditSub);
  }

  /**
   * Build sidebar links from the canonical JSON file, enriching with
   * dynamic Observable counts and mapping feature strings to enum values.
   */
  private buildSidebarLinks(): SidebarLink[] {
    const rawLinks = (sidebarLinksData as { sidebarLinks: RawSidebarLink[] }).sidebarLinks;
    return rawLinks
      .filter((link) => (environment.production ? !link.future : true))
      .map((link) => this.mapRawLink(link));
  }

  /**
   * Map a raw JSON link entry to the runtime SidebarLink with typed features and counts.
   * Handles mapping of countType to Observables and feature strings to enum values.
   * @param raw The raw sidebar link data from JSON
   * @returns The enriched SidebarLink ready for use in the template
   */
  private mapRawLink(raw: RawSidebarLink): SidebarLink {
    return {
      title: raw.title,
      route: raw.route,
      iconImg: raw.iconImg,
      count: this.resolveCount(raw),
      future: raw.future,
      isVisable: raw.isVisable,
      requiredFeature: raw.requiredFeature
        ? (raw.requiredFeature as Feature)
        : undefined,
      requiredAddon: raw.requiredAddon,
      sub_links: raw.sub_links
        ?.filter((sl) => (environment.production ? !sl.future : true))
        .map((sl) => this.mapRawLink(sl) as SidebarSubLink),
    };
  }

  /**
   * Resolve a link's count — returns the dynamic Observable for known countTypes,
   * or falls back to the static string value from JSON.
   * @param raw The raw sidebar link data which may contain countType or count
   * @returns The resolved count, either as an Observable, a string, or null
   */
  private resolveCount(raw: RawSidebarLink): Observable<string> | string | null {
    if (raw.countType) {
      switch (raw.countType) {
        case 'scanners':
          return this._scannersCount$;
        case 'scans':
          return this._scanListCount$;
        case 'ninja-scans':
          return this._ninjaScanListCount$;
        case 'assets':
          return this._assetCount$;
        default:
          return raw.count ?? null;
      }
    }
    return raw.count ?? null;
  }

  /**
   * Update tier information for sidebar display
   */
  private updateTierInfo(company: Company): void {
    this.currentTier = this.normalizeTier(company.tier);
    this.isPartnerCompany = isChannelPartnerCompany(company);

    // Set tier display name and badge class
    switch (this.currentTier) {
      case 'premium':
        // The plan the company bought, not the internal tier name (#956).
        this.tierDisplayName = planDisplayName(company.planId) ?? 'Paid plan';
        this.tierBadgeClass = 'tier-premium';
        this.dailyAiQueriesRemaining = -1; // Unlimited
        this.dailyAiQueriesTotal = -1;
        this.trialDaysRemaining = null;
        break;
      case 'trial':
        this.tierDisplayName = 'Trial';
        this.tierBadgeClass = 'tier-trial';
        this.dailyAiQueriesRemaining = -1; // Unlimited during trial
        this.dailyAiQueriesTotal = -1;
        // Calculate trial days remaining
        if (company.trialEndDate) {
          let endDate: Date;
          if ((company.trialEndDate as any) instanceof Date) {
            endDate = company.trialEndDate as unknown as Date;
          } else if (typeof (company.trialEndDate as any).toDate === 'function') {
            endDate = (company.trialEndDate as any).toDate();
          } else {
            endDate = new Date(company.trialEndDate as unknown as string);
          }
          const now = new Date();
          const diffTime = endDate.getTime() - now.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          this.trialDaysRemaining = Math.max(0, diffDays);
        }
        break;
      case 'expired':
        this.tierDisplayName = 'Expired';
        this.tierBadgeClass = 'tier-expired';
        this.dailyAiQueriesRemaining = 0;
        this.dailyAiQueriesTotal = 0;
        this.trialDaysRemaining = 0;
        break;
      case 'free':
      default: {
        // 'free' is the stored value for a company with no plan: there is no
        // free tier (#847), so no scanning and no AI until checkout.
        this.tierDisplayName = 'No Plan';
        this.tierBadgeClass = 'tier-free';
        this.dailyAiQueriesRemaining = 0;
        this.dailyAiQueriesTotal = 0;
        this.trialDaysRemaining = null;
        break;
      }
    }
  }

  /**
   * Check if user can access a specific feature
   */
  canAccessFeature(feature: Feature): boolean {
    if (!this.companyModel) {
      return false;
    }
    return this.tierAccessService.canAccessFeature(this.companyModel, feature).hasAccess;
  }

  /**
   * Check if a nav item is locked due to tier/addon restrictions
   */
  isNavItemLocked(navItem: any): boolean {
    if (!this.companyModel) {
      return false;
    }

    // Check feature-based restrictions
    if (navItem.requiredFeature) {
      return !this.canAccessFeature(navItem.requiredFeature);
    }

    // Check addon-based restrictions
    if (navItem.requiredAddon) {
      return !this.hasAddon(navItem.requiredAddon);
    }

    return false;
  }

  /**
   * Check if company has a specific addon
   * Delegates to TierAccessService for centralized addon checking logic
   */
  private hasAddon(addonId: string): boolean {
    if (!this.companyModel) {
      return false;
    }
    return this.tierAccessService.companyHasAddon(this.companyModel, addonId);
  }

  /**
   * Normalize database tier/planId values to internal tier type.
   * Delegates to TierAccessService to ensure a single source of truth
   * for tier normalization across access checks and UI.
   */
  private normalizeTier(tierValue: string | undefined): 'free' | 'trial' | 'premium' | 'expired' {
    const normalized = this.tierAccessService.normalizeTier(tierValue);
    // The enum values are guaranteed to match the literal union type
    // UserTier enum: FREE = 'free', TRIAL = 'trial', PREMIUM = 'premium', EXPIRED = 'expired'
    return normalized as 'free' | 'trial' | 'premium' | 'expired';
  }

  /**
   * Navigate to upgrade page when user clicks locked feature
   */
  onLockedFeatureClick(navItem: any): void {
    if (navItem.requiredFeature) {
      this.router.navigate(['/dashboard/company'], {
        queryParams: {
          upgrade: 'true',
          feature: navItem.requiredFeature,
          returnUrl: typeof navItem.route === 'string' ? navItem.route : navItem.route[0]
        }
      });
    }
  }

  // Toggle desktop collapsed mode
  toggleCollapse(): void {
    // Only affects desktop
    if (this.isMobile) return;
    this.collapsed = !this.collapsed;
    this.safeStorage.setItem('sidebarCollapsed', String(this.collapsed));
    this.collapsedChange.emit(this.collapsed);
    window.dispatchEvent(
      new CustomEvent('sidebar:collapsed', {
        detail: { collapsed: this.collapsed },
      }),
    );
  }

  /**
   * Returns the credit burn percentage (0–100) to drive the sidebar progress pill.
   */
  getCreditBurnPercent(): number {
    if (!this.creditSummary) return 0;
    return this.ninjaCreditsService.getBurnPercent(this.creditSummary);
  }

  /**
   * Returns the Bootstrap color token for the credit balance bar.
   */
  getCreditColorClass(): string {
    if (!this.creditSummary) return 'success';
    return this.ninjaCreditsService.getBalanceColorClass(this.creditSummary);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    // Ensure scroll lock released if sidebar was open on destroy
    if (this.isMobile && this.sideBarvisible) {
      this.scrollLock.unlock();
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(_event?: UIEvent): void {
    this.checkIfMobile();
    this.handleResponsiveChanges();
  }

  @HostListener('window:orientationchange', ['$event'])
  onOrientationChange(_event?: Event): void {
    // Handle orientation changes on mobile devices
    setTimeout(() => {
      this.checkIfMobile();
      this.handleResponsiveChanges();
    }, 100);
  }

  private checkIfMobile(): void {
    const previousMobile = this.isMobile;
    this.isMobile = window.innerWidth <= this.MOBILE_BREAKPOINT;

    // If switching from desktop to mobile and sidebar is open, close it
    if (!previousMobile && this.isMobile && this.sideBarvisible) {
      this.closeSidebarOnMobile();
    }
  }

  private handleResponsiveChanges(): void {
    if (this.isMobile && this.sideBarvisible) {
      // Ensure sidebar is properly positioned on mobile
      this.closeSidebarOnMobile();
    }
  }

  private handleNavigationEnd(): void {
    if (this.isMobile && this.sideBarvisible) {
      this.closeSidebarOnMobile();
    }
    this.setActiveMenuByRouteUrl();
  }

  private handleSidebarVisibilityChange(isVisible: boolean): void {
    if (this.isMobile && isVisible) {
      this.scrollLock.lock();
    } else {
      this.scrollLock.unlock();
    }
  }

  private closeSidebarOnMobile(): void {
    if (this.isMobile && this.sideBarvisible) {
      // Short delay for a smooth slide-out. Close explicitly rather than
      // toggling: one link click also fires NavigationEnd (and the backdrop
      // fires click, touchstart and pointerdown), and two queued toggles
      // reopened the sidebar (#826).
      setTimeout(() => {
        this.themeService.setSidebarVisibility(false);
      }, 150);
    }
  }

  // Handle navigation clicks - ensure this is called on all nav clicks
  onNavigationClick(): void {
    if (this.isMobile) {
      // Close sidebar immediately on mobile when clicking navigation
      this.closeSidebarOnMobile();
    }
  }

  // Handle submenu clicks
  onSubmenuClick(): void {
    if (this.isMobile) {
      // Close sidebar when clicking submenu items on mobile
      this.closeSidebarOnMobile();
    }
  }

  // Handle main menu toggle
  toggleActiveMenu(menuTitle: string): void {
    // Do not toggle submenu in collapsed desktop mode
    if (!this.isMobile && this.collapsed) {
      return;
    }
    // Only toggle if not navigating on mobile
    if (!this.isMobile) {
      const isClosing = this.activeMenu === menuTitle;
      this.activeMenu = isClosing ? null : menuTitle;
      if (isClosing) {
        this.activeSubMenu = null;
      }
    } else {
      // On mobile, always expand the menu but don't close sidebar yet
      const isClosing = this.activeMenu === menuTitle;
      this.activeMenu = isClosing ? null : menuTitle;
      if (isClosing) {
        this.activeSubMenu = null;
      }
    }
  }

  /**
   * Build a stable key for a second-level submenu.
   * @param parentTitle The top-level parent title
   * @param childTitle The second-level child title
   * @returns A unique submenu key
   */
  buildSubMenuKey(parentTitle: string, childTitle: string): string {
    return `${parentTitle}::${childTitle}`;
  }

  /**
   * Toggle a second-level submenu under an expanded top-level menu.
   * @param parentTitle The top-level parent title
   * @param childTitle The second-level child title
   */
  toggleActiveSubMenu(parentTitle: string, childTitle: string): void {
    if (!this.isMobile && this.collapsed) {
      return;
    }

    const key = this.buildSubMenuKey(parentTitle, childTitle);
    this.activeSubMenu = this.activeSubMenu === key ? null : key;
  }

  toggleSidebar(): void {
    this.themeService.toggleSidebarVisibility();
  }

  // Close sidebar when clicking outside (for mobile)
  onSidebarBackdropClick(): void {
    if (this.isMobile && this.sideBarvisible) {
      this.closeSidebarOnMobile();
    }
  }

  isObservable(obj: any): obj is Observable<any> {
    if (obj == null) return false;
    return isObservable(obj);
  }

  // Scanner Counts

  // Getter for scannersCount$
  get scannersCount$(): Observable<string> {
    return this._scannersCount$;
  }

  // Setter for scannersCount$ (if needed for updating the value from other parts of the app)
  set scannersCount$(value: Observable<string>) {
    this._scannersCount$ = value;
  }

  getScannersCount(): Observable<string> {
    return this.themeService.getScannersCount().pipe(
      map((scannerCount) => scannerCount?.toString() || '0'),
    );
  }

  // Scan Count
  // Getter for scanListCount$
  get scanListCount$(): Observable<string> {
    return this._scanListCount$;
  }

  // Setter for scanListCount$ (if needed for updating the value from other parts of the app)
  set scanListCount$(value: Observable<string>) {
    this._scanListCount$ = value;
  }

  getScanListCount(): Observable<string> {
    return this.themeService.getScanListCount().pipe(
      map((scanListCount) => {
        return scanListCount?.toString() || '0';
      }),
    );
  }

  /**
   * Gets the Ninja-Agent-X scan count from the control-plane API.
   * @returns Observable<string> The Ninja-Agent-X scan count as a string
   */
  getNinjaScanListCount(): Observable<string> {
    return this.ninjaAgentApiService.listScans().pipe(
      map((response) => response.total?.toString() || response.scans?.length?.toString() || '0'),
      catchError(() => of('0')),
    );
  }

  // Asset Count
  // Getter for assetCount$
  get assetCount$(): Observable<string> {
    return this._assetCount$;
  }

  // Setter for assetCount$ (if needed for updating the value from other parts of the app)
  set assetCount$(value: Observable<string>) {
    this._assetCount$ = value;
  }

  /**
   * Gets the asset count from the Tenable service.
   * The API returns a nested structure with data.total_count for the total asset count.
   * @returns Observable<string> The asset count as a string
   */
  getAssetCount(): Observable<string> {
    // getAssetList() now handles tier checking internally
    return this.tenableApiService.getAssetList().pipe(
      map((assetListResponse) => {
        if (
          assetListResponse &&
          assetListResponse.success &&
          assetListResponse.data
        ) {
          const totalCount = assetListResponse.data.total_count || 0;
          this.themeService.setAssetCount(totalCount);
          return totalCount.toString();
        }
        return '0';
      }),
    );
  }

  // -- new helpers to expand based on current URL --
  private setActiveMenuByRouteUrl(): void {
    const url = this.router.url;
    for (const link of this.sidebarLinks) {
      const activeChild = link.sub_links?.find((sl) => this.hasActiveRoute(sl, url));
      if (activeChild) {
        this.activeMenu = link.title;
        if (activeChild.sub_links?.some((child) => this.hasActiveRoute(child, url))) {
          this.activeSubMenu = this.buildSubMenuKey(link.title, activeChild.title);
        }
        break;
      }
    }
  }

  private hasActiveRoute(link: SidebarLink, url: string): boolean {
    return this.matchRoute(link.route, url)
      || !!link.sub_links?.some((child) => this.hasActiveRoute(child, url));
  }

  private matchRoute(route: string | string[], url: string): boolean {
    if (Array.isArray(route)) {
      return route.some((r) => url.startsWith(r));
    }
    return route !== '' && url.startsWith(route);
  }
  // -- end helpers --
}
